import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import ModalLayout from "@View/components/ModalLayout";
import DefaultSelect from "@View/components/formInputs/DefaultSelect";
import DefaultInput from "@View/components/formInputs/DefaultInput";
import DefaultRangeSlider from "@View/components/formInputs/DefaultRangeSlider";
import ButtonSave from "@View/components/buttons/ButtonSave";
import { FetchOnceAutocomplete } from "@Components/form/control/FetchOnceAutocomplete";
import PowerIcon from "@material-ui/icons/Power";
import getAPI from "@API/index";

const TERM_MONTHS_MIN = 1;
const TERM_MONTHS_MAX = 60;
const ALFA_BANK_INTEGRATION_KEY = "AlfaBank";
const PSB_INTEGRATION_KEY = "Psb";
const PSB_CREDIT_PRODUCT = "Кредит на пополнение оборотных средств";

const IntegrationBadge = ({ name }) => (
  <span
    title="Интеграция"
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      flexShrink: 0,
      boxSizing: "border-box",      
      height: 18,
      padding: "0 9px 0 6px",
      borderRadius: 20,
      background: "#E9F2FC",
      color: "#5195E6",
      fontSize: 12,
      fontWeight: 600,
      lineHeight: "18px",
      whiteSpace: "nowrap",
    }}
  >
    <PowerIcon style={{ fontSize: 14, flexShrink: 0 }} />
    {name}
  </span>
);

const receiverOptionNode = (receiver) => (
  <span
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      width: "100%",
      minWidth: 0,
    }}
  >
    <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
      {receiver.name}
    </span>
    {receiver.integrationName && <IntegrationBadge name={receiver.integrationName} />}
  </span>
);

const NewLeadTransferModal = ({ leadId, lead, open, handleClose, onCreated }) => {
  const { t } = useTranslation();
  const [receivers, setReceivers] = useState([]);
  const [receiverId, setReceiverId] = useState("");
  const [creditProduct, setCreditProduct] = useState("");
  const [creditProductError, setCreditProductError] = useState("");
  const [requestedAmount, setRequestedAmount] = useState("");
  const [requestedTermMonths, setRequestedTermMonths] = useState(12);
  const [city, setCity] = useState(null);
  const [cityError, setCityError] = useState("");

  const selectedReceiver = receivers.find((r) => r.value === receiverId);
  const isAlfaBank = selectedReceiver?.integrationKey === ALFA_BANK_INTEGRATION_KEY;
  const isPsb = selectedReceiver?.integrationKey === PSB_INTEGRATION_KEY;

  // У ПСБ продукт зафиксирован банком, поле не редактируется.
  const creditProductForReceiver = (integrationKey) =>
    integrationKey === PSB_INTEGRATION_KEY ? PSB_CREDIT_PRODUCT : lead?.creditProduct ?? "";

  useEffect(() => {
    if (!open) return;
    setCreditProductError("");
    setCity(null);
    setCityError("");
    setCreditProduct(lead?.creditProduct ?? "");
    setRequestedAmount(lead?.requestedAmount != null ? String(lead.requestedAmount) : "");
    setRequestedTermMonths(lead?.requestedTermMonths != null ? Number(lead.requestedTermMonths) : 12);
    getAPI()
      .request({ method: "get", url: "/api/lead/receivers", suppressError: false })
      .then((response) => {
        const list = response?.data ?? [];
        setReceivers(
          list.map((r) => ({
            value: r.id,
            name: r.name,
            node: receiverOptionNode(r),
            integrationKey: r.integrationKey,
          }))
        );
        if (list.length > 0) {
          setReceiverId(list[0].id);
          setCreditProduct(creditProductForReceiver(list[0].integrationKey));
        }
      });
  }, [open]);

  const fetchCities = useCallback(async () => {
    const response = await getAPI().request({
      method: "get",
      url: "/api/lead/integrations/alfabank/cities",
      suppressError: false,
    });
    return response?.data ?? [];
  }, []);

  const handleReceiverChange = (val) => {
    setReceiverId(val);
    setCity(null);
    setCityError("");
    setCreditProduct(creditProductForReceiver(receivers.find((r) => r.value === val)?.integrationKey));
    setCreditProductError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!receiverId) return;
    if (!creditProduct.trim()) {
      setCreditProductError("validation.CreditProductRequired");
      return;
    }
    const amountNum = Number(requestedAmount);
    const termNum = Number(requestedTermMonths);
    if (!Number.isFinite(amountNum) || amountNum <= 0) return;
    if (!Number.isInteger(termNum) || termNum <= 0) return;
    if (isAlfaBank && !city?.fias) {
      setCityError("validation.CityRequired");
      return;
    }
    getAPI()
      .request({
        method: "post",
        url: `/api/lead/${leadId}/transfer`,
        data: {
          leadReceiverCustomerId: receiverId,
          creditProduct: creditProduct.trim(),
          requestedAmount: amountNum,
          requestedTermMonths: termNum,
          cityFias: isAlfaBank ? city.fias : null,
        },
        suppressError: false,
      })
      .then((response) => {
        if (response?.data) {
          onCreated({
            ...response.data,
            leadReceiverCustomerName: response.data.leadReceiverCustomerName ?? selectedReceiver?.name,
          });
          handleClose();
        }
      });
  };

  return (
    <ModalLayout
      open={open}
      onClose={handleClose}
      title="h1.New Transfer"
      onSubmit={handleSubmit}
      footer={<ButtonSave marginTop="0">buttons.Save</ButtonSave>}
    >
      <DefaultSelect
        label="inputs.LeadReceiver"
        value={receiverId}
        dropdowns={receivers}
        onChange={handleReceiverChange}
      />
      {isAlfaBank && (
        <FetchOnceAutocomplete
          id="alfaBankCity"
          label={t("inputs.City")}
          fetchOptions={fetchCities}
          value={city}
          onChange={(val) => {
            setCity(val);
            if (cityError) setCityError("");
          }}
          error={!!cityError}
          helperText={cityError ? t(cityError) : ""}
        />
      )}
      <DefaultInput
        label="inputs.CreditProduct"
        value={creditProduct}
        onChange={(val) => {
          setCreditProduct(val);
          if (creditProductError) setCreditProductError("");
        }}
        disabled={isPsb}
        error={!!creditProductError}
        helperText={creditProductError}
      />
      <DefaultInput
        label="inputs.RequestedAmount"
        value={requestedAmount}
        onChange={(val) => setRequestedAmount(val)}
        type="number"
        required
      />
      <DefaultRangeSlider
        label="inputs.TermMonths"
        value={requestedTermMonths}
        onChange={(val) => setRequestedTermMonths(val)}
        min={TERM_MONTHS_MIN}
        max={TERM_MONTHS_MAX}
      />
    </ModalLayout>
  );
};

export default NewLeadTransferModal;
